import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sprout, Users, Stethoscope, ListChecks, MapPin, AlertTriangle, Syringe, ChevronRight } from "lucide-react";import { resolveImage } from "../lib/images";

const VAX_HIDDEN_ROLES = new Set(["driver"]);
const VET_APPTS_HIDDEN_ROLES = new Set(["groom", "driver"]);
const fmtDate = (iso) => {
  try { return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};
const STATUS_BADGE = {
  overdue: { label: "Overdue", cls: "se-badge-urgent" },
  due_soon: { label: "Due soon", cls: "se-badge-warning" },
  upcoming: { label: "Upcoming", cls: "se-badge-active" },
};

function UpcomingVaccinations() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/dashboard/upcoming-vaccinations?limit=5")
      .then((r) => { if (!cancelled) setItems(r.data); })
      .catch((e) => { if (!cancelled) { setErr(formatApiError(e)); setItems([]); } });
    return () => { cancelled = true; };
  }, []);

  if (err) return null;

  return (
    <div className="se-card mt-6" data-testid="upcoming-vaccinations-widget">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div>
          <div className="se-label flex items-center gap-2"><Syringe size={12} /> Health</div>
          <h3 className="font-heading mt-1" style={{ fontSize: 20 }}>Next vaccinations due</h3>
        </div>
        {items && items.length > 0 && (
          <span className="text-xs" style={{ color: "#8C9E8A" }} data-testid="upcoming-vaccinations-count">
            {items.length} upcoming
          </span>
        )}
      </div>
      {items === null && (
        <div className="text-sm" style={{ color: "#8C9E8A" }}>Loading…</div>
      )}
      {items !== null && items.length === 0 && (
        <div className="text-sm" style={{ color: "#8C9E8A" }} data-testid="upcoming-vaccinations-empty">
          No vaccinations scheduled. Activate vaccines from a horse profile to start tracking.
        </div>
      )}
      <div className="space-y-2">
        {(items || []).map((it, idx) => {
          const badge = STATUS_BADGE[it.status] || STATUS_BADGE.upcoming;
          const subline =
            it.status === "overdue"
              ? `Overdue by ${Math.abs(it.days_until_due)} day${Math.abs(it.days_until_due) === 1 ? "" : "s"}`
              : it.days_until_due === 0
              ? "Due today"
              : `In ${it.days_until_due} day${it.days_until_due === 1 ? "" : "s"} · ${fmtDate(it.next_due_date)}`;
          return (
            <button
              key={`${it.horse_id}-${it.vaccine_name}-${idx}`}
              type="button"
              onClick={() => navigate(`/horses/${it.horse_id}`)}
              className="w-full flex items-center gap-3 p-3 rounded-lg text-left"
              style={{ background: "rgba(45,110,110,0.06)", border: "0.5px solid rgba(45,110,110,0.18)", cursor: "pointer" }}
              data-testid={`upcoming-vaccination-row-${idx}`}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: "rgba(45,110,110,0.15)", color: "#2D6E6E" }}
              >
                <Syringe size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span style={{ fontWeight: 600 }}>{it.horse_name}</span>
                  <span className="text-xs" style={{ color: "#8C9E8A" }}>· {it.vaccine_name}</span>
                </div>
                <div className="text-xs mt-0.5" style={{ color: "#8C9E8A" }}>{subline}</div>
              </div>
              <span className={`se-badge ${badge.cls}`}>{badge.label}</span>
              <ChevronRight size={16} style={{ color: "#8C9E8A" }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NextVetVisits() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/dashboard/vet-appointments")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) { setErr(formatApiError(e)); setData({ count: 0, appointments: [] }); } });
    return () => { cancelled = true; };
  }, []);

  if (err && !data) return null;

  const items = data?.appointments || [];
  return (
    <div className="se-card mt-6" data-testid="next-vet-visits-widget">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div>
          <div className="se-label flex items-center gap-2"><Stethoscope size={12} /> Health</div>
          <h3 className="font-heading mt-1" style={{ fontSize: 20 }}>Next vet visits</h3>
        </div>
        {items.length > 0 && (
          <span className="text-xs" style={{ color: "#8C9E8A" }} data-testid="next-vet-visits-count">
            {items.length} in next 14 days
          </span>
        )}
      </div>
      {data === null && (
        <div className="text-sm" style={{ color: "#8C9E8A" }}>Loading…</div>
      )}
      {data !== null && items.length === 0 && (
        <div className="text-sm" style={{ color: "#8C9E8A" }} data-testid="next-vet-visits-empty">
          No upcoming vet appointments in the next 14 days
        </div>
      )}
      <div className="space-y-2">
        {items.map((it, idx) => (
          <button
            key={`${it.horse_id}-${it.next_appointment_date}-${idx}`}
            type="button"
            onClick={() => navigate(`/horses/${it.horse_id}`)}
            className="w-full flex items-center gap-3 p-3 rounded-lg text-left"
            style={{ background: "rgba(232,168,56,0.08)", border: "0.5px solid rgba(232,168,56,0.28)", cursor: "pointer" }}
            data-testid={`next-vet-visit-row-${idx}`}
          >
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: "rgba(232,168,56,0.18)", color: "#8C6B14" }}
            >
              <Stethoscope size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span style={{ fontWeight: 600 }} data-testid={`next-vet-visit-horse-${idx}`}>{it.horse_name}</span>
                <span className="text-xs" style={{ color: "#8C9E8A" }}>· {it.vet_name}</span>
              </div>
              <div className="text-xs mt-0.5" style={{ color: "#8C9E8A" }} data-testid={`next-vet-visit-date-${idx}`}>
                {fmtDate(it.next_appointment_date)}
              </div>
            </div>
            <ChevronRight size={16} style={{ color: "#8C9E8A" }} />
          </button>
        ))}
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, accent, testid, onClick }) => (
  <button
    onClick={onClick}
    type="button"
    className="se-card text-left"
    style={{ cursor: "pointer", transition: "transform 150ms ease, box-shadow 150ms ease" }}
    onMouseDown={(e) => e.currentTarget.style.transform = "translateY(1px)"}
    onMouseUp={(e) => e.currentTarget.style.transform = "translateY(0)"}
    data-testid={testid}
    aria-label={`${label} — open details`}
  >
    <div className="flex items-start justify-between">
      <div>
        <div className="se-label">{label}</div>
        <div className="font-heading mt-2" style={{ fontSize: 28 }}>{value}</div>
      </div>
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center"
        style={{ background: `${accent}22`, color: accent }}
      >
        <Icon size={20} />
      </div>
    </div>
    <div className="text-xs mt-3" style={{ color: "#8C9E8A" }}>Tap to open →</div>
  </button>
);

function VetSpendYtdCard() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/dashboard/vet-spend-ytd")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setHidden(true); });
    return () => { cancelled = true; };
  }, []);

  if (hidden) return null;

  const amount = data?.total_vet_spend_ytd ?? null;
  const yearLabel = data?.year != null ? String(data.year) : "";
  const formatted = amount == null
    ? "—"
    : new Intl.NumberFormat("en-IE", { style: "currency", currency: data?.currency || "EUR" }).format(amount);
  const subline = amount === 0
    ? "€0.00 — no vet costs recorded this year."
    : yearLabel ? `YTD spend for ${yearLabel}` : "";

  return (
    <div
      className="se-card text-left"
      style={{
        cursor: "default",
        background: "rgba(232,168,56,0.10)",
        border: "0.5px solid rgba(232,168,56,0.32)",
      }}
      data-testid="stat-vet-spend-ytd"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="se-label">Vet spend YTD</div>
          <div className="font-heading mt-2" style={{ fontSize: 28, color: "#8C6B14", fontVariantNumeric: "tabular-nums" }} data-testid="stat-vet-spend-ytd-value">
            {formatted}
          </div>
        </div>
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "rgba(232,168,56,0.22)", color: "#8C6B14" }}
        >
          <Stethoscope size={20} />
        </div>
      </div>
      <div className="text-xs mt-3" style={{ color: "#8C6B14" }} data-testid="stat-vet-spend-ytd-subline">
        {subline}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [stable, setStable] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, st] = await Promise.all([
          api.get("/dashboard/stats"),
          user?.stable_id ? api.get("/stables/me") : Promise.resolve({ data: null }),
        ]);
        if (cancelled) return;
        setStats(s.data);
        setStable(st.data);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const banner = resolveImage(stable?.banner_url);
  const logo = resolveImage(stable?.logo_url);

  return (
    <div data-testid="dashboard-page">
      {stable && (
        <div
          className="mb-6 rounded-2xl overflow-hidden relative"
          style={{
            aspectRatio: "5 / 1",
            background: banner ? `center/cover no-repeat url(${banner})` : "linear-gradient(135deg, #7C5C3A 0%, #B8895A 100%)",
            minHeight: 120,
          }}
          data-testid="dashboard-banner"
        >
          <div
            style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(0deg, rgba(59,47,30,0.45) 0%, rgba(59,47,30,0.05) 100%)",
            }}
          />
          <div className="absolute inset-0 flex items-end justify-between px-5 md:px-8 pb-4 md:pb-6 gap-3">
            <div className="flex items-end gap-3">
              {logo && (
                <div
                  style={{
                    width: 56, height: 56, borderRadius: 12,
                    background: "#FDFAF6", padding: 4,
                    border: "0.5px solid rgba(212,169,106,0.4)",
                  }}
                >
                  <img src={logo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
                </div>
              )}
              <div>
                <h1 className="font-heading" style={{ fontSize: 32, color: "#F5EFE6", lineHeight: 1.1 }} data-testid="dashboard-heading">
                  {stable.name}
                </h1>
                <p className="text-sm mt-1" style={{ color: "#F5EFE6", opacity: 0.85 }}>
                  {stable.area_region ? `${stable.area_region} · ` : ""}{stable.country}
                </p>
              </div>
            </div>
            <span
              className={`se-badge ${stable.status === "trial" ? "se-badge-warning" : "se-badge-active"}`}
              data-testid="dashboard-stable-status"
            >
              {stable.plan} · {stable.status}
            </span>
          </div>
        </div>
      )}

      {!stable && (
        <div className="mb-6">
          <div className="se-label">Overview</div>
          <h1 className="font-heading mt-1" style={{ fontSize: 32 }} data-testid="dashboard-heading">
            All stables
          </h1>
          <p className="text-sm mt-1" style={{ color: "#8C9E8A" }}>
            {user?.role === "super_admin"
              ? "Platform-wide view across all tenants."
              : `Welcome back, ${user?.full_name}.`}
          </p>
        </div>
      )}

      {error && (
        <div
          className="mb-6 text-sm px-3 py-2 rounded-lg"
          style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }}
          data-testid="dashboard-error"
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
        {user?.role === "owner" && <VetSpendYtdCard />}
        <StatCard icon={Sprout} label="Total Horses"
          value={stats ? stats.total_horses : "—"}
          accent="#7C5C3A" testid="stat-total-horses"
          onClick={() => navigate("/horses")} />
        <StatCard icon={Users} label="Active Staff"
          value={stats ? stats.active_staff : "—"}
          accent="#4A7C59" testid="stat-active-staff"
          onClick={() => navigate("/staff")} />
        <StatCard icon={AlertTriangle} label="Care Alerts"
          value={stats ? stats.care_alerts : "—"}
          accent="#C0392B" testid="stat-care-alerts"
          onClick={() => navigate("/horses?alert=care")} />
        <StatCard icon={MapPin} label="Open Trips"
          value={stats ? stats.open_trips : "—"}
          accent="#2D6E6E" testid="stat-open-trips"
          onClick={() => navigate("/trips?status=open")} />
        {!VET_APPTS_HIDDEN_ROLES.has(user?.role) && (
          <StatCard icon={Stethoscope} label="Vet Appts (14 days)"
            value={stats ? stats.upcoming_vet_appointments : "—"}
            accent="#E8A838" testid="stat-vet-appts"
            onClick={() => navigate("/horses")} />
        )}
        <StatCard icon={ListChecks} label="Open Jobs"
          value={stats ? stats.open_jobs : "—"}
          accent="#7C5C3A" testid="stat-open-jobs"
          onClick={() => navigate("/jobs")} />
      </div>

      {!VET_APPTS_HIDDEN_ROLES.has(user?.role) && <NextVetVisits />}

      {!VAX_HIDDEN_ROLES.has(user?.role) && <UpcomingVaccinations />}

      {stable && stable.google_maps_url && (
        <a
          href={stable.google_maps_url}
          target="_blank" rel="noreferrer"
          className="se-card mt-6 flex items-center gap-3"
          style={{ textDecoration: "none", color: "#3B2F1E" }}
          data-testid="dashboard-maps-card"
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(124,92,58,0.15)", color: "#7C5C3A" }}>
            <MapPin size={20} />
          </div>
          <div className="flex-1">
            <div className="se-label">Location</div>
            <div className="text-sm">Open in Google Maps</div>
          </div>
          <span className="se-link text-sm">View ↗</span>
        </a>
      )}
    </div>
  );
}
